import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  GoogleMap,
  MapAdvancedMarker,
  MapInfoWindow,
  MapMarker,
} from '@angular/google-maps';
import { AsyncPipe } from '@angular/common';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { TuiLetModule } from '@taiga-ui/cdk';
import {
  TuiButtonModule,
  TuiDialogContext,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import {
  TuiComboBoxModule,
  TuiDataListWrapperModule,
  TuiInputModule,
} from '@taiga-ui/kit';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GoogleMapService } from '@shared/services/google-map.service';

type Geo = google.maps.GeocoderResult;

export type LocationSelectInputData = {
  coords?: Nullable<string>;
};

export type LocationSelectOutputData = {
  latLng: google.maps.LatLngLiteral;
  address: Nullable<string>;
};
@Component({
  selector: 'app-location-select',
  standalone: true,
  imports: [
    GoogleMap,
    AsyncPipe,
    MapMarker,
    TuiLetModule,
    MapAdvancedMarker,
    TuiButtonModule,
    TuiTextfieldControllerModule,
    ReactiveFormsModule,
    TuiInputModule,
    TuiComboBoxModule,
    TuiDataListWrapperModule,
    MapInfoWindow,
  ],
  providers: [GoogleMapService],
  templateUrl: './location-select.component.html',
  styleUrl: './location-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSelectComponent implements OnInit {
  @ViewChild(GoogleMap) map!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  search$ = new BehaviorSubject<Nullable<string>>('');
  search = this.fb.control<Nullable<Geo>>(null);
  options$ = new BehaviorSubject<Nullable<google.maps.MapOptions>>(
    this.googleMapService.getInitialMapOptions(),
  );

  markerOptions: google.maps.marker.AdvancedMarkerElementOptions = {
    gmpDraggable: false,
  };

  markerPosition$ = new BehaviorSubject<Nullable<google.maps.LatLngLiteral>>(
    null,
  );

  positionAddress$ = new BehaviorSubject<Nullable<string>>(null);

  results$: Observable<Geo[]> = this.search$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    filter(value => value !== null),
    switchMap(search => this.googleMapService.geocodeByAddress(search ?? '')),
    map(response => response.results),
    startWith([]),
  );

  constructor(
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      LocationSelectOutputData,
      LocationSelectInputData
    >,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    protected readonly googleMapService: GoogleMapService,
  ) {}

  onSearchChange(searchQuery: string | null): void {
    this.search$.next(searchQuery);
  }

  async onMapClick(event: google.maps.MapMouseEvent) {
    const coords = event.latLng?.toJSON();
    if (coords) {
      const address = await this.googleMapService.loadAddress(
        coords.lat,
        coords.lng,
      );
      this.positionAddress$.next(address);
    }
    this.markerPosition$.next(coords ?? null);
  }

  onSubmit() {
    if (this.markerPosition$.value) {
      this.context.$implicit.next({
        latLng: this.markerPosition$.value,
        address: this.positionAddress$.value,
      });
      this.context.$implicit.complete();
    }
  }

  onSelectValue() {
    this.search.valueChanges
      .pipe(
        filter((result): result is Geo => result !== null),
        tap(result => {
          const { lat, lng } = result.geometry.location.toJSON();
          this.positionAddress$.next(result.formatted_address);
          this.markerPosition$.next({ lat, lng });
          this.zoomOnSelect({ lat, lng });
          this.search.setValue(null, { emitEvent: false });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  async openInfoWindow(marker: MapAdvancedMarker) {
    this.infoWindow.open(marker);
  }

  zoomOnSelect(center: { lat: number; lng: number }) {
    this.options$.next({
      ...this.options$.value,
      zoom: 10,
      center,
    });
  }

  setInitialLocation() {
    if (this.context.data?.coords) {
      this.search$.next(this.context.data.coords);
      of(this.context.data.coords)
        .pipe(
          take(1),
          switchMap(search => this.googleMapService.geocodeByAddress(search)),
          tap(response => {
            if (response.results.length) {
              const [result] = response.results;
              this.positionAddress$.next(result.formatted_address);
              this.markerPosition$.next(result.geometry.location.toJSON());
            }
          }),
        )
        .subscribe();
    }
  }

  ngOnInit() {
    this.onSelectValue();
    this.setInitialLocation();
  }
}
